import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { api } from '../utils/api';
import { Upload, FileSpreadsheet, CheckCircle, AlertTriangle, X, Download, Eye, ChevronRight, RefreshCw, ChevronDown, ChevronUp, Edit2, Layers, Sparkles, HelpCircle } from 'lucide-react';
import { toast } from 'sonner';
import BACKEND_BASE from '../utils/backend';

const BACKEND = BACKEND_BASE;

const PRODUCT_TYPE_LABELS = {
  degraissant: { label: 'Degraissant', color: '#EA580C', bg: '#FFF7ED' },
  desinfectant: { label: 'Desinfectant', color: '#DC2626', bg: '#FEF2F2' },
  detergent: { label: 'Detergent', color: '#0891B2', bg: '#ECFEFF' },
  detartrant: { label: 'Detartrant', color: '#7C3AED', bg: '#F5F3FF' },
  multi_usage: { label: 'Multi-usage', color: '#059669', bg: '#ECFDF5' },
  // Legacy classifier types (backward compat)
  degreaser: { label: 'Degraissant', color: '#EA580C', bg: '#FFF7ED' },
  general_cleaner: { label: 'Nettoyant', color: '#0891B2', bg: '#ECFEFF' },
  cold_cleaner: { label: 'Froid', color: '#2563EB', bg: '#EFF6FF' },
  floor_cleaner: { label: 'Sol', color: '#7C3AED', bg: '#F5F3FF' },
  dishwasher: { label: 'Plonge', color: '#0D9488', bg: '#F0FDFA' },
  surface_spray: { label: 'Spray', color: '#6366F1', bg: '#EEF2FF' },
  disinfectant: { label: 'Desinfectant', color: '#DC2626', bg: '#FEF2F2' },
  rinse_aid: { label: 'Rincage', color: '#059669', bg: '#ECFDF5' },
  waste_cleaner: { label: 'Dechets', color: '#78716C', bg: '#F5F5F4' },
  unknown: { label: 'Non classe', color: '#94A3B8', bg: '#F8FAFC' },
};

const ImportExcel = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [planName, setPlanName] = useState('');
  const [result, setResult] = useState(null);
  const [collapsedZones, setCollapsedZones] = useState({});
  const [editingRow, setEditingRow] = useState(null);
  // Smart matching: track user validations (raw_alias -> surface_id) to learn from
  const [validatedAliases, setValidatedAliases] = useState({});

  const handleFile = useCallback(async (file) => {
    if (!file) return;
    const ext = file.name.split('.').pop().toLowerCase();
    if (!['xlsx', 'csv'].includes(ext)) {
      toast.error('Format non supporte. Utilisez .xlsx ou .csv');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/import/parse', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setPreviewData(res.data);
      const clientName = res.data.client?.name || '';
      setPlanName(clientName ? `Plan HACCP ${clientName}` : 'Plan HACCP Import');
      setStep(2);
      toast.success(`${res.data.total_rows} lignes generees automatiquement`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors du parsing');
    } finally {
      setUploading(false);
    }
  }, []);

  const onDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    handleFile(e.dataTransfer.files[0]);
  }, [handleFile]);

  const onFileInput = useCallback((e) => {
    handleFile(e.target.files[0]);
  }, [handleFile]);

  const updateRow = (rowIndex, field, value) => {
    setPreviewData(prev => {
      const newRows = [...prev.rows];
      const idx = newRows.findIndex(r => r.row_index === rowIndex);
      if (idx === -1) return prev;
      const row = { ...newRows[idx] };
      row[field] = value;

      if (field === 'product_id') {
        const prod = prev.products_available.find(p => p.id === value);
        row.product_matched = prod ? prod.name : null;
        row.product_name = prod ? prod.name : '';
        row.warnings = (row.warnings || []).filter(w => !w.includes('produit') && !w.includes('Produit') && !w.includes('revue'));
        row.status = (row.warnings || []).length > 0 ? 'warning' : 'ok';
        if (value) {
          setEditingRow(null);
        }
      }
      if (field === 'frequency_id') {
        const freq = prev.frequencies_available.find(f => f.id === value);
        row.frequency_name = freq ? freq.name : '';
      }
      if (field === 'method_id') {
        const meth = prev.methods_available?.find(m => m.id === value);
        row.method_name = meth ? meth.name : '';
      }

      newRows[idx] = row;
      return {
        ...prev,
        rows: newRows,
        ok_rows: newRows.filter(r => r.status === 'ok').length,
        warning_rows: newRows.filter(r => r.status !== 'ok').length,
      };
    });
  };

  // Smart matching: user accepts a proposed surface match. Locks the row's
  // surface_id + icon_id, clears the proposal, and queues the alias for
  // learning on the next /import/generate call.
  const acceptSurfaceProposal = (rowIndex) => {
    setPreviewData(prev => {
      const newRows = [...prev.rows];
      const idx = newRows.findIndex(r => r.row_index === rowIndex);
      if (idx === -1) return prev;
      const row = { ...newRows[idx] };
      const prop = row.surface_match_proposal;
      if (!prop) return prev;
      row.surface_id = prop.candidate_id;
      row.surface_name = prop.candidate_name;
      row.icon_id = prop.candidate_icon_id || null;
      const rawAlias = row.surface_excel_text || row.surface_name;
      row.surface_match_proposal = null;
      row.warnings = (row.warnings || []).filter(
        w => !w.toLowerCase().includes('a valider') && !w.toLowerCase().includes('aucune correspondance')
      );
      row.status = (row.warnings || []).length > 0 ? 'warning' : 'ok';
      newRows[idx] = row;
      setValidatedAliases((al) => ({ ...al, [rawAlias]: prop.candidate_id }));
      return {
        ...prev,
        rows: newRows,
        ok_rows: newRows.filter(r => r.status === 'ok').length,
        warning_rows: newRows.filter(r => r.status !== 'ok').length,
        review_required_count: Math.max(0, (prev.review_required_count || 0) - 1),
        auto_matched_count: (prev.auto_matched_count || 0) + 1,
      };
    });
  };

  // User picks a different surface manually from the dropdown. Same learning
  // semantics: persist the alias so next import recognizes the Excel text.
  const pickSurfaceForRow = (rowIndex, surfaceId) => {
    setPreviewData(prev => {
      const newRows = [...prev.rows];
      const idx = newRows.findIndex(r => r.row_index === rowIndex);
      if (idx === -1) return prev;
      const row = { ...newRows[idx] };
      const surface = (prev.surfaces_available || []).find(s => s.id === surfaceId);
      if (!surface) return prev;
      const rawAlias = row.surface_excel_text || row.surface_name;
      row.surface_id = surface.id;
      row.surface_name = surface.name;
      row.icon_id = surface.icon_id || null;
      row.surface_match_proposal = null;
      row.warnings = (row.warnings || []).filter(
        w => !w.toLowerCase().includes('a valider') && !w.toLowerCase().includes('aucune correspondance')
      );
      row.status = (row.warnings || []).length > 0 ? 'warning' : 'ok';
      newRows[idx] = row;
      setValidatedAliases((al) => ({ ...al, [rawAlias]: surface.id }));
      return {
        ...prev,
        rows: newRows,
        ok_rows: newRows.filter(r => r.status === 'ok').length,
        warning_rows: newRows.filter(r => r.status !== 'ok').length,
        review_required_count: Math.max(0, (prev.review_required_count || 0) - 1),
        auto_matched_count: (prev.auto_matched_count || 0) + 1,
      };
    });
  };

  const removeRow = (rowIndex) => {
    setPreviewData(prev => {
      const newRows = prev.rows.filter(r => r.row_index !== rowIndex);
      return {
        ...prev,
        rows: newRows,
        total_rows: newRows.length,
        ok_rows: newRows.filter(r => r.status === 'ok').length,
        warning_rows: newRows.filter(r => r.status !== 'ok').length,
      };
    });
  };

  const toggleZone = (zoneName) => {
    setCollapsedZones(prev => ({ ...prev, [zoneName]: !prev[zoneName] }));
  };

  const handleGenerate = async () => {
    if (!previewData || previewData.rows.length === 0) {
      toast.error('Aucune ligne a importer');
      return;
    }
    if (previewData.client?.missing) {
      toast.error('Le nom du client est manquant dans le fichier Excel');
      return;
    }
    setGenerating(true);
    try {
      // Send validated aliases so the server learns them for future imports.
      const aliases_to_learn = Object.entries(validatedAliases)
        .filter(([raw, sid]) => raw && sid)
        .map(([raw_alias, surface_id]) => ({ raw_alias, surface_id }));
      const res = await api.post('/import/generate', {
        // Keep title-separator rows even when zone_id is null:
        // the backend auto-creates the zone on save.
        rows: previewData.rows.filter(r => r.zone_id || r.is_zone_title || r.zone_name),
        plan_name: planName || `Plan HACCP ${previewData.client?.name || 'Import'}`,
        client: previewData.client,
        aliases_to_learn,
      });
      setResult(res.data);
      setStep(3);
      toast.success('Plan genere avec succes !');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la generation');
    } finally {
      setGenerating(false);
    }
  };

  // Group rows by zone for display
  const groupedByZone = previewData ? previewData.rows.reduce((acc, row) => {
    // Skip title-separator rows from grouping — the zone header already visualizes them.
    if (row.is_zone_title) return acc;
    const zone = row.zone_matched || row.zone_name || 'Sans zone';
    if (!acc[zone]) acc[zone] = [];
    acc[zone].push(row);
    return acc;
  }, {}) : {};

  const ProductTypeBadge = ({ type }) => {
    const info = PRODUCT_TYPE_LABELS[type] || PRODUCT_TYPE_LABELS.unknown;
    return (
      <span
        className="text-xs px-1.5 py-0.5 rounded font-medium whitespace-nowrap"
        style={{ backgroundColor: info.bg, color: info.color }}
      >
        {info.label}
      </span>
    );
  };

  return (
    <Layout>
      <div data-testid="import-excel-page" className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold" style={{ color: '#0E2D43' }}>Import Excel</h1>
          <button
            onClick={() => { window.location.href = `${BACKEND}/api/import/template`; }}
            data-testid="download-template-btn"
            className="inline-flex items-center gap-2 px-4 py-2 rounded text-sm font-medium border"
            style={{ borderColor: '#CBD5E1', color: '#334155' }}
          >
            <Download size={16} />
            Telecharger le modele
          </button>
        </div>

        {/* Progress */}
        <div className="flex items-center gap-2 mb-8">
          {[{ n: 1, label: 'Upload' }, { n: 2, label: 'Verification' }, { n: 3, label: 'Resultat' }].map((s, i) => (
            <React.Fragment key={s.n}>
              {i > 0 && <ChevronRight size={16} style={{ color: '#CBD5E1' }} />}
              <div
                className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium"
                style={{
                  backgroundColor: step >= s.n ? '#0E2D43' : '#F1F5F9',
                  color: step >= s.n ? '#FFFFFF' : '#64748B',
                }}
              >
                <span>{s.n}</span>
                <span>{s.label}</span>
              </div>
            </React.Fragment>
          ))}
        </div>

        {/* ============ STEP 1: Upload ============ */}
        {step === 1 && (
          <div data-testid="step-upload">
            <div
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={onDrop}
              className="border-2 border-dashed rounded-xl p-16 text-center cursor-pointer transition-colors"
              style={{
                borderColor: dragOver ? '#2563EB' : '#CBD5E1',
                backgroundColor: dragOver ? '#EFF6FF' : '#FAFBFC',
              }}
              onClick={() => document.getElementById('file-input').click()}
            >
              <input id="file-input" type="file" accept=".xlsx,.csv" className="hidden" onChange={onFileInput} data-testid="file-input" />
              {uploading ? (
                <div className="flex flex-col items-center gap-3">
                  <RefreshCw size={40} className="animate-spin" style={{ color: '#2563EB' }} />
                  <p className="text-base font-medium" style={{ color: '#334155' }}>Analyse et generation en cours...</p>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-4">
                  <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ backgroundColor: '#EFF6FF' }}>
                    <FileSpreadsheet size={32} style={{ color: '#2563EB' }} />
                  </div>
                  <div>
                    <p className="text-lg font-semibold" style={{ color: '#0E2D43' }}>Glissez votre fichier Excel ici</p>
                    <p className="text-sm mt-1" style={{ color: '#64748B' }}>ou cliquez pour parcourir (.xlsx, .csv)</p>
                  </div>
                </div>
              )}
            </div>

            <div className="mt-8 bg-white border rounded-lg p-6" style={{ borderColor: '#E2E8F0' }}>
              <h3 className="font-semibold mb-3" style={{ color: '#0E2D43' }}>Format attendu (4 colonnes)</h3>
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <p className="text-sm font-medium mb-2" style={{ color: '#16A34A' }}>Obligatoires</p>
                  <ul className="space-y-1.5 text-sm" style={{ color: '#334155' }}>
                    <li><code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs font-mono">client_name</code> Nom exact du client</li>
                    <li><code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs font-mono">zone</code> Zone (Poste chaud, Plonge...)</li>
                    <li><code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs font-mono">product</code> Produit utilise</li>
                  </ul>
                </div>
                <div>
                  <p className="text-sm font-medium mb-2" style={{ color: '#94A3B8' }}>Optionnel</p>
                  <ul className="space-y-1.5 text-sm" style={{ color: '#334155' }}>
                    <li><code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs font-mono">city</code> Ville du client</li>
                  </ul>
                  <div className="mt-4 p-3 rounded-lg text-xs" style={{ backgroundColor: '#F8FAFC', color: '#64748B' }}>
                    L'application assigne automatiquement les produits aux surfaces de chaque zone.
                    Plusieurs produits par surface sont supportes (ex: degraissant + desinfectant).
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ============ STEP 2: Preview ============ */}
        {step === 2 && previewData && (
          <div data-testid="step-preview">
            {/* Client warning if missing */}
            {previewData.client?.missing && (
              <div className="mb-4 p-4 rounded-lg border flex items-center gap-3" style={{ backgroundColor: '#FEF2F2', borderColor: '#FECACA' }}>
                <AlertTriangle size={20} style={{ color: '#DC2626' }} />
                <span className="text-sm font-medium" style={{ color: '#991B1B' }}>
                  Colonne <code className="bg-white px-1 rounded">client_name</code> manquante ou vide dans le fichier Excel. Veuillez corriger et re-importer.
                </span>
              </div>
            )}

            {/* Smart matching summary (2026-05) */}
            {(previewData.auto_matched_count > 0 || previewData.review_required_count > 0 || previewData.unmatched_count > 0) && (
              <div data-testid="smart-match-summary"
                className="bg-white border rounded-lg p-4 mb-4 flex items-center gap-4"
                style={{ borderColor: '#E2E8F0' }}>
                <Sparkles size={22} style={{ color: '#7C3AED' }} />
                <div className="flex-1">
                  <p className="text-sm font-semibold mb-1" style={{ color: '#0E2D43' }}>
                    Reconnaissance intelligente des surfaces
                  </p>
                  <div className="flex flex-wrap gap-2 items-center text-xs">
                    {previewData.auto_matched_count > 0 && (
                      <span data-testid="match-auto-count"
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full"
                        style={{ backgroundColor: '#F0FDF4', color: '#15803D' }}>
                        <CheckCircle size={11} />
                        {previewData.auto_matched_count} surface(s) reconnue(s) automatiquement
                      </span>
                    )}
                    {previewData.review_required_count > 0 && (
                      <span data-testid="match-review-count"
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full"
                        style={{ backgroundColor: '#FEF3C7', color: '#92400E' }}>
                        <HelpCircle size={11} />
                        {previewData.review_required_count} a valider
                      </span>
                    )}
                    {previewData.unmatched_count > 0 && (
                      <span data-testid="match-unmatched-count"
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full"
                        style={{ backgroundColor: '#FEE2E2', color: '#991B1B' }}>
                        <AlertTriangle size={11} />
                        {previewData.unmatched_count} surface(s) inconnue(s)
                      </span>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Summary */}
            <div className="bg-white border rounded-lg p-5 mb-6" style={{ borderColor: '#E2E8F0' }}>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <span className="text-sm" style={{ color: '#64748B' }}>Client :</span>
                  <span className="ml-2 font-semibold" data-testid="client-name-display" style={{ color: '#0E2D43' }}>
                    {previewData.client?.name || '(manquant)'}
                    {previewData.client?.city && <span className="font-normal ml-2" style={{ color: '#64748B' }}>({previewData.client.city})</span>}
                  </span>
                  {previewData.client?.exists && (
                    <span className="ml-2 text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: '#F0FDF4', color: '#15803D' }}>existant</span>
                  )}
                  {!previewData.client?.exists && previewData.client?.name && (
                    <span className="ml-2 text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: '#EFF6FF', color: '#1D4ED8' }}>nouveau</span>
                  )}
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg" style={{ backgroundColor: '#F0FDF4' }}>
                    <CheckCircle size={16} style={{ color: '#16A34A' }} />
                    <span className="text-sm font-medium" style={{ color: '#15803D' }}>{previewData.ok_rows} OK</span>
                  </div>
                  {previewData.warning_rows > 0 && (
                    <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg" style={{ backgroundColor: '#FFFBEB' }}>
                      <AlertTriangle size={16} style={{ color: '#D97706' }} />
                      <span className="text-sm font-medium" style={{ color: '#92400E' }}>{previewData.warning_rows} avertissement(s)</span>
                    </div>
                  )}
                  <span className="text-sm" style={{ color: '#94A3B8' }}>{previewData.total_rows} lignes</span>
                </div>
              </div>

              {/* Imported products recap */}
              {previewData.imported_products && (
                <div className="flex flex-wrap gap-2 pt-3 border-t" style={{ borderColor: '#F1F5F9' }}>
                  <span className="text-xs" style={{ color: '#94A3B8' }}>Produits importes :</span>
                  {Object.values(previewData.imported_products).flat().filter((v, i, a) => a.indexOf(v) === i).map((p, i) => (
                    <span key={i} className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: '#F1F5F9', color: '#334155' }}>{p}</span>
                  ))}
                </div>
              )}

              {/* Multi-product indicator */}
              {previewData.total_rows > Object.values(groupedByZone).reduce((acc, rows) => {
                const surfaces = new Set(rows.map(r => r.surface_id || r.surface_name));
                return acc + surfaces.size;
              }, 0) && (
                <div className="flex items-center gap-2 mt-3 pt-3 border-t" style={{ borderColor: '#F1F5F9' }}>
                  <Layers size={14} style={{ color: '#7C3AED' }} />
                  <span className="text-xs" style={{ color: '#7C3AED' }}>
                    Multi-produits detectes : plusieurs produits assignes par surface
                  </span>
                </div>
              )}
            </div>

            {/* Plan name */}
            <div className="mb-5">
              <label className="block text-sm font-medium mb-1.5" style={{ color: '#334155' }}>Nom du plan</label>
              <input
                type="text"
                value={planName}
                onChange={(e) => setPlanName(e.target.value)}
                data-testid="plan-name-input"
                className="w-full max-w-md px-3 py-2 border rounded-lg text-sm"
                style={{ borderColor: '#CBD5E1' }}
              />
            </div>

            {/* Grouped by zone */}
            {Object.entries(groupedByZone).map(([zoneName, zoneRows]) => {
              // Count unique surfaces to show multi-product info
              const surfaceCounts = {};
              zoneRows.forEach(r => {
                const key = r.surface_id || r.surface_name;
                surfaceCounts[key] = (surfaceCounts[key] || 0) + 1;
              });
              const hasMultiProduct = Object.values(surfaceCounts).some(c => c > 1);

              return (
                <div key={zoneName} className="mb-4 border rounded-lg overflow-hidden" style={{ borderColor: '#E2E8F0' }}>
                  {/* Zone header */}
                  <button
                    onClick={() => toggleZone(zoneName)}
                    data-testid={`zone-toggle-${zoneName}`}
                    className="w-full flex items-center justify-between px-4 py-3 text-left"
                    style={{ backgroundColor: '#F1F5F9' }}
                  >
                    <div className="flex items-center gap-3">
                      {collapsedZones[zoneName] ? <ChevronRight size={18} style={{ color: '#64748B' }} /> : <ChevronDown size={18} style={{ color: '#64748B' }} />}
                      <span className="font-semibold text-sm" style={{ color: '#0E2D43' }}>{zoneName}</span>
                      <span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: '#E2E8F0', color: '#475569' }}>
                        {zoneRows.length} ligne(s)
                      </span>
                      {hasMultiProduct && (
                        <span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: '#F5F3FF', color: '#7C3AED' }}>
                          multi-produits
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {zoneRows.every(r => r.status === 'ok') ? (
                        <CheckCircle size={16} style={{ color: '#16A34A' }} />
                      ) : (
                        <AlertTriangle size={16} style={{ color: '#D97706' }} />
                      )}
                    </div>
                  </button>

                  {/* Zone rows */}
                  {!collapsedZones[zoneName] && (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm" style={{ minWidth: '1000px' }}>
                        <thead>
                          <tr style={{ backgroundColor: '#0F2A3F' }}>
                            <th className="px-3 py-2 text-left text-xs font-medium text-white">Surface</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-white">Produit assigne</th>
                            <th className="px-3 py-2 text-center text-xs font-medium text-white" style={{ width: '90px' }}>Type</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-white">Methode</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-white">Frequence</th>
                            <th className="px-3 py-2 text-center text-xs font-medium text-white" style={{ width: '60px' }}>Statut</th>
                            <th className="px-3 py-2 text-center text-xs font-medium text-white" style={{ width: '70px' }}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {zoneRows.map((row, rIdx) => {
                            const isEditing = editingRow === row.row_index;
                            const sameAsPrev = rIdx > 0 && zoneRows[rIdx - 1].surface_name === row.surface_name && zoneRows[rIdx - 1].surface_id === row.surface_id;

                            return (
                              <tr
                                key={row.row_index}
                                data-testid={`preview-row-${row.row_index}`}
                                className="border-t"
                                style={{
                                  borderColor: '#F1F5F9',
                                  backgroundColor: row.status === 'review' ? '#FDF4FF' : row.status !== 'ok' ? '#FFFBEB' : '#FFFFFF',
                                }}
                              >
                                <td className="px-3 py-2.5 text-sm" style={{ color: '#0F172A' }}>
                                  {sameAsPrev ? (
                                    <span className="text-xs italic" style={{ color: '#94A3B8' }}>idem</span>
                                  ) : row.surface_match_proposal ? (
                                    <div data-testid={`surface-proposal-${row.row_index}`} className="space-y-1">
                                      <div className="text-xs" style={{ color: '#92400E' }}>
                                        Excel : <span className="font-semibold">{row.surface_excel_text || row.surface_name}</span>
                                      </div>
                                      <div className="inline-flex items-center gap-1.5 px-2 py-1 rounded"
                                        style={{ backgroundColor: '#FEF3C7', border: '1px solid #FCD34D' }}>
                                        <Sparkles size={11} style={{ color: '#92400E' }} />
                                        <span className="text-xs font-medium" style={{ color: '#92400E' }}>
                                          {row.surface_match_proposal.candidate_name} ({Math.round(row.surface_match_proposal.score * 100)}%)
                                        </span>
                                      </div>
                                      <div className="flex items-center gap-1">
                                        <button
                                          onClick={() => acceptSurfaceProposal(row.row_index)}
                                          data-testid={`accept-proposal-${row.row_index}`}
                                          className="text-[10px] px-2 py-0.5 rounded font-medium"
                                          style={{ backgroundColor: '#D1FAE5', color: '#065F46' }}
                                        >
                                          ✓ Valider
                                        </button>
                                        <select
                                          value=""
                                          onChange={(e) => {
                                            if (e.target.value) pickSurfaceForRow(row.row_index, e.target.value);
                                          }}
                                          data-testid={`pick-surface-${row.row_index}`}
                                          className="text-[10px] px-1 py-0.5 rounded border"
                                          style={{ borderColor: '#D1D5DB' }}
                                        >
                                          <option value="">Autre...</option>
                                          {(previewData.surfaces_available || []).map(s => (
                                            <option key={s.id} value={s.id}>{s.name}</option>
                                          ))}
                                        </select>
                                      </div>
                                    </div>
                                  ) : (
                                    <span className="font-medium">{row.surface_name || '-'}</span>
                                  )}
                                </td>
                                <td className="px-3 py-2.5">
                                  {isEditing ? (
                                    <select
                                      value={row.product_id || ''}
                                      onChange={(e) => updateRow(row.row_index, 'product_id', e.target.value)}
                                      autoFocus
                                      data-testid={`product-select-${row.row_index}`}
                                      className="w-full px-2 py-1 text-xs border rounded"
                                      style={{ borderColor: '#2563EB', backgroundColor: '#EFF6FF' }}
                                    >
                                      <option value="">-- Choisir un produit --</option>
                                      {previewData.products_available.map(p => (
                                        <option key={p.id} value={p.id}>{p.name}</option>
                                      ))}
                                    </select>
                                  ) : row.product_matched ? (
                                    <span className="text-sm" style={{ color: '#0F172A' }}>{row.product_matched}</span>
                                  ) : row.status === 'review' ? (
                                    <select
                                      value={row.product_id || ''}
                                      onChange={(e) => updateRow(row.row_index, 'product_id', e.target.value)}
                                      data-testid={`product-select-${row.row_index}`}
                                      className="w-full px-2 py-1 text-xs border rounded"
                                      style={{ borderColor: '#D946EF', backgroundColor: '#FDF4FF' }}
                                    >
                                      <option value="">-- Revue manuelle --</option>
                                      {previewData.products_available.map(p => (
                                        <option key={p.id} value={p.id}>{p.name}</option>
                                      ))}
                                    </select>
                                  ) : (
                                    <select
                                      value={row.product_id || ''}
                                      onChange={(e) => updateRow(row.row_index, 'product_id', e.target.value)}
                                      data-testid={`product-select-${row.row_index}`}
                                      className="w-full px-2 py-1 text-xs border rounded"
                                      style={{ borderColor: '#FCD34D', backgroundColor: '#FFFBEB' }}
                                    >
                                      <option value="">-- Choisir un produit --</option>
                                      {previewData.products_available.map(p => (
                                        <option key={p.id} value={p.id}>{p.name}</option>
                                      ))}
                                    </select>
                                  )}
                                </td>
                                <td className="px-3 py-2.5 text-center">
                                  {row.product_type && <ProductTypeBadge type={row.product_type} />}
                                </td>
                                <td className="px-3 py-2.5">
                                  {row.method_name ? (
                                    <span className="text-xs" style={{ color: '#475569' }}>{row.method_name}</span>
                                  ) : (
                                    <select
                                      value={row.method_id || ''}
                                      onChange={(e) => updateRow(row.row_index, 'method_id', e.target.value)}
                                      className="w-full px-2 py-1 text-xs border rounded"
                                      style={{ borderColor: '#CBD5E1' }}
                                    >
                                      <option value="">-- Auto --</option>
                                      {(previewData.methods_available || []).map(m => (
                                        <option key={m.id} value={m.id}>{m.name}</option>
                                      ))}
                                    </select>
                                  )}
                                </td>
                                <td className="px-3 py-2.5">
                                  {row.frequency_name ? (
                                    <span className="text-xs px-2 py-1 rounded-full font-medium" style={{ backgroundColor: '#EFF6FF', color: '#1D4ED8' }}>
                                      {row.frequency_name}
                                    </span>
                                  ) : (
                                    <select
                                      value={row.frequency_id || ''}
                                      onChange={(e) => updateRow(row.row_index, 'frequency_id', e.target.value)}
                                      className="w-full px-2 py-1 text-xs border rounded"
                                      style={{ borderColor: '#CBD5E1' }}
                                    >
                                      <option value="">-- Auto --</option>
                                      {previewData.frequencies_available.map(f => (
                                        <option key={f.id} value={f.id}>{f.name}</option>
                                      ))}
                                    </select>
                                  )}
                                </td>
                                <td className="px-3 py-2.5 text-center">
                                  {row.status === 'ok' ? (
                                    <CheckCircle size={16} style={{ color: '#16A34A' }} />
                                  ) : row.status === 'review' ? (
                                    <div title="Revue manuelle requise">
                                      <Edit2 size={16} style={{ color: '#D946EF' }} />
                                    </div>
                                  ) : (
                                    <div title={(row.warnings || []).join(', ')}>
                                      <AlertTriangle size={16} style={{ color: '#D97706' }} />
                                    </div>
                                  )}
                                </td>
                                <td className="px-3 py-2.5 text-center">
                                  <div className="flex items-center justify-center gap-1">
                                    {row.product_matched && !isEditing && (
                                      <button
                                        onClick={() => setEditingRow(row.row_index)}
                                        className="p-1 rounded hover:bg-blue-50"
                                        title="Remplacer le produit"
                                        data-testid={`edit-product-${row.row_index}`}
                                      >
                                        <Edit2 size={13} style={{ color: '#2563EB' }} />
                                      </button>
                                    )}
                                    <button
                                      onClick={() => removeRow(row.row_index)}
                                      className="p-1 rounded hover:bg-red-50"
                                      title="Supprimer cette ligne"
                                      data-testid={`remove-row-${row.row_index}`}
                                    >
                                      <X size={14} style={{ color: '#EF4444' }} />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })}

            {/* Actions */}
            <div className="flex items-center justify-between mt-6">
              <button
                onClick={() => { setStep(1); setPreviewData(null); setEditingRow(null); }}
                data-testid="back-to-upload"
                className="px-4 py-2.5 rounded-lg text-sm font-medium border"
                style={{ borderColor: '#CBD5E1', color: '#334155' }}
              >
                Recommencer
              </button>
              <button
                onClick={handleGenerate}
                disabled={generating || previewData.rows.length === 0 || previewData.client?.missing}
                data-testid="generate-plan-btn"
                className="inline-flex items-center gap-2 px-6 py-2.5 rounded-lg text-sm font-semibold disabled:opacity-50"
                style={{ backgroundColor: '#0E2D43', color: '#FFFFFF' }}
              >
                {generating ? (
                  <><RefreshCw size={16} className="animate-spin" /> Generation...</>
                ) : (
                  <><CheckCircle size={16} /> Valider et generer le plan</>
                )}
              </button>
            </div>
          </div>
        )}

        {/* ============ STEP 3: Done ============ */}
        {step === 3 && result && (
          <div data-testid="step-result" className="text-center py-12">
            <div className="w-20 h-20 rounded-full mx-auto mb-6 flex items-center justify-center" style={{ backgroundColor: '#F0FDF4' }}>
              <CheckCircle size={40} style={{ color: '#16A34A' }} />
            </div>
            <h2 className="text-2xl font-bold mb-2" style={{ color: '#0E2D43' }}>Plan genere !</h2>
            <p className="text-base mb-1" style={{ color: '#334155' }}>
              <strong>{result.plan_name}</strong> pour <strong>{result.client_name}</strong>
            </p>
            <p className="text-sm mb-8" style={{ color: '#64748B' }}>{result.lines_created} ligne(s) creee(s)</p>
            <div className="flex items-center justify-center gap-4">
              <button
                onClick={() => navigate(`/plans/${result.plan_id}/edit`)}
                data-testid="edit-generated-plan"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold"
                style={{ backgroundColor: '#FBBF24', color: '#0E2D43' }}
              >
                Editer le plan avant generation
              </button>
              <button
                onClick={() => {
                  const token = localStorage.getItem('access_token');
                  if (token) window.open(`${BACKEND}/api/plans/${result.plan_id}/pdf?mode=preview&token=${encodeURIComponent(token)}`, '_blank');
                }}
                data-testid="view-generated-pdf"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold"
                style={{ backgroundColor: '#0E2D43', color: '#FFFFFF' }}
              >
                <Eye size={16} /> Voir le PDF
              </button>
              <button onClick={() => navigate('/plans')} data-testid="go-to-plans"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium border"
                style={{ borderColor: '#CBD5E1', color: '#334155' }}>
                Voir tous les plans
              </button>
              <button onClick={() => { setStep(1); setPreviewData(null); setResult(null); setEditingRow(null); }} data-testid="import-another"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium"
                style={{ backgroundColor: '#EFF6FF', color: '#1D4ED8' }}>
                <Upload size={16} /> Nouvel import
              </button>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default ImportExcel;
